/* eslint-disable max-len */
import ComunidadesRuraisTradicionaisAPI from 'api/forms/comunidades_rurais_tradicionais';
import { PacoteStatusEnum } from 'enum/pacoteStatus.enum';
import { FormularioComunidades } from 'interfaces/formularioComunidades.interface';
import { create } from 'zustand';

export interface IComunidadesRuraisTradicionais {
  uuid: string
  createdAt: string
  updatedAt: string
  deletedAt: any
  pacote_status: number
  step_atual: number
  institution_uuid: string
  current_form_unique_id?: string | null
  current_add_type?: 'assentamentos' | 'comunidades' | null
  current_index?: number | null

  // Step 1
  ano_referencia_do_questionario: number | null
  tipo_cadastro: number | null
  tipo_de_assentamento: number | null
  codigo_do_pa: number | null
  nome_do_assentamento: string | null
  quantidade_de_familias: number | null

  // Step 2
  possui_energia_eletrica: boolean | null
  possui_saneamento_basico: boolean | null
  coleta_de_lixo: boolean | null
  recebeu_manutencao_das_estradas_no_ultimo_ano: boolean | null
  possui_escolas: boolean | null
  possui_esgotamento_sanitario: boolean | null
  possui_cultura_manejo_solo: boolean | null
  tem_degradacao_solo: boolean | null
  tem_assoreamento_fluvial: boolean | null
  escola_json: Array<Array<{ jurisdicao: string; nome: string }>> | null
  possui_oferta_de_transporte_escolar_para_os_estudantes: boolean | null
  possui_unidade_de_saude: boolean | null
  recebeu_agentes_de_saude_do_municipio: boolean | null

  // Step 3
  possui_agua_para_abastecimento_domestico: boolean | null
  qual_fonte_da_agua_domestico: number | null
  possui_agua_para_producao_agropecuaria: boolean | null
  qual_fonte_da_producao_agropecuaria: number | null
  quantas_familia_estao_sem_acesso_agua_domestico: number | null
  quantas_familia_estao_sem_acesso_agua_producao: number | null
  comunidade_quilomba_tem_dificil_acesso_agua_domestico: boolean | null
  comunidade_quilomba_tem_dificil_acesso_agua_producao: boolean | null
  tem_acesso_internet: boolean | null

  created_by: string
  updated_by: string
  deleted_by: any
  pacote_formularios_uuid: string

  // NOVO MODELO
  assentamentos: Array<FormularioComunidades>
  comunidades: Array<FormularioComunidades>
}

export interface IComunidadesRuraisTradicionaisStore {
  comunidadesRuraisTradicionais: IComunidadesRuraisTradicionais,
  loading: boolean,
  setComunidadesRuraisTradicionais: (comunidadesRuraisTradicionais: IComunidadesRuraisTradicionais) => void
  getComunidadesRuraisTradicionais: (uuid: string) => Promise<IComunidadesRuraisTradicionais>,
  setPartialForm: (chave: keyof IComunidadesRuraisTradicionais, valor: any) => void,
  setLoading: (loading: boolean) => void,

  saveStep1: () => Promise<boolean>,
  saveStep2: () => Promise<boolean>,
  saveStep3: () => Promise<boolean>,
  salvarComoCadastrado: () => Promise<boolean>;
  enviarAprovacao: () => Promise<boolean>,

  saveDraftForm: () => Promise<boolean>,
}

// === HELPERS ===
const isFilled = (v: any) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
};

const pickFilled = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: Partial<T> = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (isFilled(v)) (out as any)[k] = v;
  });
  return out;
};

const mapToFormularioComunidades = (s: IComunidadesRuraisTradicionais): FormularioComunidades => {
  const uniqueId =
    (s as any).current_form_unique_id ||
    (s as any).unique_id ||
    String(Date.now());

  return {
    unique_id: uniqueId,
    step_atual: s.step_atual ?? 3,

    // Step1
    ano_referencia: (s.ano_referencia_do_questionario ?? undefined) as any,
    tipo_cadastro: s.tipo_cadastro ?? null,
    tipo_de_assentamento: s.tipo_de_assentamento ?? null,
    codigo_do_pa: (isFilled(s.codigo_do_pa) ? String(s.codigo_do_pa) : null),
    nome_do_assentamento: s.nome_do_assentamento ?? null,
    quantidade_de_familias: s.quantidade_de_familias ?? null,

    // Step2
    possui_energia_eletrica: s.possui_energia_eletrica ?? null,
    possui_saneamento_basico: s.possui_saneamento_basico ?? null,
    coleta_de_lixo: s.coleta_de_lixo ?? null,
    recebeu_manutencao_das_estradas_no_ultimo_ano: s.recebeu_manutencao_das_estradas_no_ultimo_ano ?? null,
    possui_escolas: s.possui_escolas ?? null,
    quantidade_escolas: Array.isArray(s.escola_json) ? s.escola_json.flat().length : null,
    escolas: Array.isArray(s.escola_json)
      ? s.escola_json.flat().map((e) => ({ nome: e.nome, jurisdicao: e.jurisdicao }))
      : [],
    possui_esgotamento_sanitario: s.possui_esgotamento_sanitario ?? null,
    possui_cultura_manejo_solo: s.possui_cultura_manejo_solo ?? null,
    tem_degradacao_solo: s.tem_degradacao_solo ?? null,
    tem_assoreamento_fluvial: s.tem_assoreamento_fluvial ?? null,
    possui_oferta_de_transporte_escolar_para_os_estudantes:
      s.possui_oferta_de_transporte_escolar_para_os_estudantes ?? null,
    possui_unidade_de_saude: s.possui_unidade_de_saude ?? null,
    recebeu_agentes_de_saude_do_municipio: s.recebeu_agentes_de_saude_do_municipio ?? null,

    // Step3
    tem_acesso_internet: s.tem_acesso_internet ?? null,
    possui_agua_para_abastecimento_domestico: s.possui_agua_para_abastecimento_domestico ?? null,
    qual_fonte_da_agua_domestico: s.qual_fonte_da_agua_domestico ?? null,
    possui_agua_para_producao_agropecuaria: s.possui_agua_para_producao_agropecuaria ?? null,
    qual_fonte_da_producao_agropecuaria: s.qual_fonte_da_producao_agropecuaria ?? null,
    quantas_familia_estao_sem_acesso_agua_domestico: s.quantas_familia_estao_sem_acesso_agua_domestico ?? null,
    quantas_familia_estao_sem_acesso_agua_producao: s.quantas_familia_estao_sem_acesso_agua_producao ?? null,
    comunidade_quilomba_tem_dificil_acesso_agua_domestico: s.comunidade_quilomba_tem_dificil_acesso_agua_domestico ?? null,
    comunidade_quilomba_tem_dificil_acesso_agua_producao: s.comunidade_quilomba_tem_dificil_acesso_agua_producao ?? null,
  };
};

const useComunidadesRuraisTradicionaisStore = create<IComunidadesRuraisTradicionaisStore>((set, get) => ({
  comunidadesRuraisTradicionais: {} as IComunidadesRuraisTradicionais,
  loading: true,

  setComunidadesRuraisTradicionais: (comunidadesRuraisTradicionais) => set({ comunidadesRuraisTradicionais }),

  getComunidadesRuraisTradicionais: async (uuid: string) => {
    set({ loading: true });
    const response = await ComunidadesRuraisTradicionaisAPI.getByUUID(uuid);
    const data = response.dados;

    console.log('[GET] tem_acesso_internet do servidor =>', data?.tem_acesso_internet);

    const normalized = {
      ...data,
      assentamentos: Array.isArray(data.assentamentos) ? data.assentamentos : [],
      comunidades: Array.isArray(data.comunidades) ? data.comunidades : [],
      current_form_unique_id: null,
      current_add_type: null,
      current_index: null,
    };

    set({ comunidadesRuraisTradicionais: normalized, loading: false });
    return normalized;
  },

  salvarComoCadastrado: async () => {
    const s = get().comunidadesRuraisTradicionais;

    const patch: Partial<IComunidadesRuraisTradicionais> = {
      assentamentos: Array.isArray(s.assentamentos) ? s.assentamentos : [],
      comunidades: Array.isArray(s.comunidades) ? s.comunidades : [],
      step_atual: 3,
      pacote_status: PacoteStatusEnum.CADASTRADO,

      // limpa marcadores de edição
      current_form_unique_id: null,
      current_add_type: null,
      current_index: null,
    };

    const resp = await ComunidadesRuraisTradicionaisAPI.update(s.uuid, patch);
    if (resp.Status === 'OK') {
      set((state) => ({
        comunidadesRuraisTradicionais: {
          ...state.comunidadesRuraisTradicionais,
          ...patch,
        }
      }));
      return true;
    }
    return false;
  },




  setPartialForm: (chave, valor) => {
    console.log('[STORE:setPartialForm]', chave, '=>', valor); // 👈 logger
    set((state) => ({
      comunidadesRuraisTradicionais: {
        ...state.comunidadesRuraisTradicionais,
        [chave]: valor
      }
    }))
  },


  setLoading: (loading) => set({ loading }),

  // ====== STEP 1 ======
  saveStep1: async () => {
    const s = get().comunidadesRuraisTradicionais;

    const patchStep1 = pickFilled({
      ano_referencia_do_questionario: s.ano_referencia_do_questionario,
      tipo_cadastro: s.tipo_cadastro,
      tipo_de_assentamento: s.tipo_de_assentamento,
      codigo_do_pa: s.codigo_do_pa,
      nome_do_assentamento: s.nome_do_assentamento,
      quantidade_de_familias: s.quantidade_de_familias,
    });

    const patch = {
      ...patchStep1,
      step_atual: 2,
      pacote_status: PacoteStatusEnum.RASCUNHO,
    };

    const resp = await ComunidadesRuraisTradicionaisAPI.update(s.uuid, patch);
    if (resp.Status === 'OK') {
      set((state) => ({
        comunidadesRuraisTradicionais: {
          ...state.comunidadesRuraisTradicionais,
          ...patch,
        }
      }));
      return true;
    }
    return false;
  },

  // ====== STEP 2 ======
  saveStep2: async () => {
    const s = get().comunidadesRuraisTradicionais;

    const patchStep2 = pickFilled({
      possui_energia_eletrica: s.possui_energia_eletrica,
      possui_saneamento_basico: s.possui_saneamento_basico,
      coleta_de_lixo: s.coleta_de_lixo,
      recebeu_manutencao_das_estradas_no_ultimo_ano: s.recebeu_manutencao_das_estradas_no_ultimo_ano,
      possui_escolas: s.possui_escolas,
      possui_esgotamento_sanitario: s.possui_esgotamento_sanitario,
      possui_cultura_manejo_solo: s.possui_cultura_manejo_solo,
      tem_degradacao_solo: s.tem_degradacao_solo,
      tem_assoreamento_fluvial: s.tem_assoreamento_fluvial,
      escola_json: s.escola_json,
      possui_oferta_de_transporte_escolar_para_os_estudantes: s.possui_oferta_de_transporte_escolar_para_os_estudantes,
      possui_unidade_de_saude: s.possui_unidade_de_saude,
      recebeu_agentes_de_saude_do_municipio: s.recebeu_agentes_de_saude_do_municipio,
      tem_acesso_internet: s.tem_acesso_internet,
    });

    const patch = {
      ...patchStep2,
      step_atual: 3,
      pacote_status: PacoteStatusEnum.RASCUNHO,
    };

    const resp = await ComunidadesRuraisTradicionaisAPI.update(s.uuid, patch);
    if (resp.Status === 'OK') {
      set((state) => ({
        comunidadesRuraisTradicionais: {
          ...state.comunidadesRuraisTradicionais,
          ...patch,
        }
      }));
      return true;
    }
    return false;
  },



  // ====== STEP 3 ======
  saveStep3: async () => {
    const s = get().comunidadesRuraisTradicionais;

    const patchStep3 = pickFilled({
      possui_agua_para_abastecimento_domestico: s.possui_agua_para_abastecimento_domestico,
      qual_fonte_da_agua_domestico: s.qual_fonte_da_agua_domestico,
      possui_agua_para_producao_agropecuaria: s.possui_agua_para_producao_agropecuaria,
      qual_fonte_da_producao_agropecuaria: s.qual_fonte_da_producao_agropecuaria,
      quantas_familia_estao_sem_acesso_agua_domestico: s.quantas_familia_estao_sem_acesso_agua_domestico,
      quantas_familia_estao_sem_acesso_agua_producao: s.quantas_familia_estao_sem_acesso_agua_producao,
      comunidade_quilomba_tem_dificil_acesso_agua_domestico: s.comunidade_quilomba_tem_dificil_acesso_agua_domestico,
      comunidade_quilomba_tem_dificil_acesso_agua_producao: s.comunidade_quilomba_tem_dificil_acesso_agua_producao,
    });

    const mergedLocal = {
      ...s,
      ...patchStep3,
      step_atual: 3,
    } as IComunidadesRuraisTradicionais;

    const formFinal = mapToFormularioComunidades(mergedLocal);

    const isAssentamento = mergedLocal.tipo_cadastro === 1;
    const arrayKey: 'assentamentos' | 'comunidades' = isAssentamento ? 'assentamentos' : 'comunidades';

    // lista atual no store
    const atual = get().comunidadesRuraisTradicionais;
    const lista = Array.isArray(atual[arrayKey]) ? [...atual[arrayKey]] : [];

    console.log('FormFinal ID:', formFinal.unique_id);
    console.log('Lista atual:', lista.map(x => x.unique_id));


    // 1) prioriza índice vindo do resumo (edição)
    let editIdx =
      typeof s.current_index === 'number' && s.current_index >= 0 && s.current_index < lista.length
        ? s.current_index
        : -1;

    // 2) se não tiver índice, tenta por unique_id (para itens já criados com unique_id)
    if (editIdx < 0 && formFinal.unique_id) {
      editIdx = lista.findIndex((x: any) => x?.unique_id && x.unique_id === formFinal.unique_id);
    }

    // 3) substitui se achou; caso contrário, adiciona
    if (editIdx >= 0) {
      const prev = lista[editIdx] || {};
      // preserva o unique_id existente do slot quando houver, senão usa o do formFinal
      const keepId = (prev as any).unique_id || formFinal.unique_id;
      lista[editIdx] = { ...formFinal, unique_id: keepId };
    } else {
      lista.push(formFinal);
    }

    const patch: Partial<IComunidadesRuraisTradicionais> = {
      ...patchStep3,
      [arrayKey]: lista,
      step_atual: 3,
      pacote_status: PacoteStatusEnum.RASCUNHO,

      // limpa marcadores de edição após salvar
      current_form_unique_id: null,
      current_add_type: null,
      current_index: null,
    };

    const resp = await ComunidadesRuraisTradicionaisAPI.update(s.uuid, patch);

    if (resp.Status === 'OK') {
      set((state) => ({
        comunidadesRuraisTradicionais: {
          ...state.comunidadesRuraisTradicionais,
          ...patch,
        }
      }));
      return true;
    }
    return false;
  },



  // ====== ENVIAR APROVAÇÃO ======
  enviarAprovacao: async () => {
    const s = get().comunidadesRuraisTradicionais;

    const mergedLocal: IComunidadesRuraisTradicionais = {
      ...s,
      step_atual: 3,
      pacote_status: PacoteStatusEnum.CADASTRADO ?? 3,
    };

    const formFinal: FormularioComunidades = mapToFormularioComunidades(mergedLocal);

    const isAssentamento = mergedLocal.tipo_cadastro === 1;
    const arrayKey: 'assentamentos' | 'comunidades' = isAssentamento ? 'assentamentos' : 'comunidades';

    const atual = get().comunidadesRuraisTradicionais;
    const lista = Array.isArray(atual[arrayKey]) ? [...atual[arrayKey]] : [];
    const idx = lista.findIndex(x => x.unique_id === formFinal.unique_id);
    if (idx >= 0) {
      lista[idx] = formFinal;
    } else {
      lista.push(formFinal);
    }


    const patchAprovacao: Partial<IComunidadesRuraisTradicionais> = {
      ...pickFilled(mergedLocal),
      [arrayKey]: lista,
      step_atual: 3,
      pacote_status: PacoteStatusEnum.CADASTRADO ?? 3,
    };

    const resp = await ComunidadesRuraisTradicionaisAPI.update(s.uuid, patchAprovacao);
    if (resp.Status === 'OK') {
      set((state) => ({
        comunidadesRuraisTradicionais: {
          ...state.comunidadesRuraisTradicionais,
          ...patchAprovacao,
        }
      }));
      return true;
    }
    return false;
  },

  saveDraftForm: async () => {
    const form = get().comunidadesRuraisTradicionais;
    const resp = await ComunidadesRuraisTradicionaisAPI.update(form.uuid, {
      ...pickFilled(form),
      pacote_status: PacoteStatusEnum.RASCUNHO,
    });
    return resp.Status === 'OK';
  },
}));

export default useComunidadesRuraisTradicionaisStore;
